/**
 * @mhmo91/ican — recording proxy.
 *
 * The condition callback `(data, me) => Cond` runs **once** at module
 * load. Every property access lands on this proxy and returns a
 * `FieldRef`-like object whose comparison methods append `Cond` nodes
 * to the rule's AST. The AST is frozen on the entity; nothing here
 * runs again on `me.can(...)`.
 *
 * The proxy itself has no state. Each rule callback gets fresh
 * proxies, but they share no memory — the only output is the AST node
 * the callback returns.
 */
import type { Cond, FieldPath, FieldRef, Literal, ParentProxy, WorkspaceProxy } from './types.ts';

const FIELD_PATH = Symbol.for('@mhmo91/ican/fieldPath');

type Tagged = { readonly [FIELD_PATH]: FieldPath };

const isTagged = (value: unknown): value is Tagged =>
  typeof value === 'object' && value !== null && FIELD_PATH in value;

/**
 * Read the captured FieldPath off a `FieldRef` produced by this
 * module. Used by `cmp`/`in`/`has*` to detect a right-hand side that
 * is itself a field reference (`data.X.eq(me.X)`).
 */
export const fieldPathOf = (value: unknown): FieldPath | undefined =>
  isTagged(value) ? value[FIELD_PATH] : undefined;

const toRhs = (value: unknown): Literal | FieldPath => {
  const fp = fieldPathOf(value);
  if (fp !== undefined) return fp;
  return value as Literal;
};

const cmp =
  (op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte') =>
  (left: FieldPath, right: unknown): Cond => ({
    kind: 'cmp',
    op,
    left,
    right: toRhs(right),
  });

const inOp = (left: FieldPath, values: readonly unknown[]): Cond => ({
  kind: 'in',
  left,
  values: values as readonly Literal[],
});

const listOp =
  (kind: 'hasAny' | 'hasAll' | 'hasOnly') =>
  (left: FieldPath, values: readonly unknown[]): Cond => ({
    kind,
    left,
    values: values as readonly Literal[],
  });

const matchesOp = (left: FieldPath, regex: string): Cond => ({
  kind: 'matches',
  left,
  regex,
});

/**
 * Array-membership check against a dynamic actor FieldRef.
 * `data.sharedWith.containsMember(me.uid)` builds:
 *   `{ kind: 'containsMember', left: ['data','sharedWith'], actorRef: ['me','uid'] }`.
 * The right-hand side is stored as a FieldPath (extracted from the
 * recording proxy's tagged object) so the emitter and runtime can
 * resolve the actor's value at call time rather than freezing a literal.
 */
const containsMemberOp = (left: FieldPath, ref: unknown): Cond => {
  const actorRef = fieldPathOf(ref);
  if (actorRef === undefined) {
    throw new Error(
      '@mhmo91/ican: containsMember() requires a FieldRef produced by the recording proxy ' +
        '(e.g. me.uid), not a literal value. For constant-list membership use .hasAny([value]).',
    );
  }
  return { kind: 'containsMember', left, actorRef };
};

/** Comparison-method names — every other property access on a FieldRef
 * is treated as a nested field step (`data.sell.visibility`), not a
 * comparator call. */
const METHOD_NAMES = new Set([
  'exists',
  'eq',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'hasAny',
  'hasAll',
  'hasOnly',
  'matches',
  'containsMember',
]);

/**
 * Build a FieldRef for a given path. The returned object exposes
 * every method on `FieldRef<T>` regardless of T — TypeScript narrows
 * which methods are visible at the call site; the runtime is
 * permissive (a misuse caught by TS would never reach here). It is
 * also a Proxy: any property access that isn't a comparison method
 * (`.eq`, `.hasAny`, …) is treated as a nested-field step and returns
 * a fresh `FieldRef` at the extended path (`data.sell.visibility`) —
 * so a rule chains arbitrarily deep into a nested schema object.
 */
const makeFieldRef = (path: FieldPath): FieldRef<unknown> => {
  const methods = {
    exists: (): Cond => ({ kind: 'exists', left: path }),
    eq: (v: unknown) => cmp('eq')(path, v),
    ne: (v: unknown) => cmp('ne')(path, v),
    gt: (v: unknown) => cmp('gt')(path, v),
    gte: (v: unknown) => cmp('gte')(path, v),
    lt: (v: unknown) => cmp('lt')(path, v),
    lte: (v: unknown) => cmp('lte')(path, v),
    in: (values: readonly unknown[]) => inOp(path, values),
    hasAny: (values: readonly unknown[]) => listOp('hasAny')(path, values),
    hasAll: (values: readonly unknown[]) => listOp('hasAll')(path, values),
    hasOnly: (values: readonly unknown[]) => listOp('hasOnly')(path, values),
    matches: (regex: string) => matchesOp(path, regex),
    containsMember: (ref: unknown) => containsMemberOp(path, ref),
  };
  return new Proxy(methods, {
    get(target, key) {
      if (key === FIELD_PATH) return path;
      if (typeof key === 'symbol') return undefined;
      if (METHOD_NAMES.has(key)) return (target as Record<string, unknown>)[key];
      return makeFieldRef([...path, key] as unknown as FieldPath);
    },
    has(target, key) {
      return key === FIELD_PATH || key in target;
    },
  }) as unknown as FieldRef<unknown>;
};

/**
 * Build a FieldRef for a named Firestore path variable.
 * The path is `['pathVar', name]`; it emits as the bare variable name
 * in Firestore rules and resolves to `undefined` at runtime.
 */
export const makePathVarRef = (name: string, segment?: number): FieldRef<string> => {
  if (segment !== undefined && (!Number.isInteger(segment) || segment < 0)) throw new Error('@mhmo91/ican: path segment must be a nonnegative integer');
  return makeFieldRef(segment === undefined ? ['pathVar', name] : ['pathVar', name, String(segment)]) as unknown as FieldRef<string>;
};

export const makeParentRoot = <P>(name: string): ParentProxy<P> => {
  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) throw new Error('@mhmo91/ican: parent name must be an identifier');
  return new Proxy(Object.create(null) as object, {
    get(_target, key) {
      if (typeof key === 'symbol') return undefined;
      return makeFieldRef(['parent', name, key]);
    },
  }) as ParentProxy<P>;
};

/**
 * Build a recording proxy rooted at `data` or `me`. Each property
 * access on the root returns a `FieldRef` whose path starts
 * `[source, key]`; a `FieldRef` is itself chainable, so
 * `data.sell.visibility` extends the path to `['data','sell','visibility']`.
 * The runtime evaluator (`evaluate.ts`) walks arbitrary-depth paths
 * already; the Firestore emitter (`visitor.ts`) joins the trailing
 * segments with `.` for `data.X`.
 */
export const makeRoot = (source: 'data' | 'me' | 'workspace'): unknown =>
  new Proxy(Object.create(null) as object, {
    get(_target, key) {
      if (typeof key === 'symbol') return undefined;
      return makeFieldRef([source, key]);
    },
  });

/**
 * Build a recording proxy rooted at the workspace document the entity's own
 * document lives under, so `workspace<Workspace>().published` captures
 * `['workspace','published']`.
 *
 * The type argument is the caller's; `makeRoot` erases to `unknown` at this
 * boundary the same way `makePathVarRef` does, and every path built through
 * it is checked against `W` at the call site.
 */
export const makeWorkspaceRoot = <W>(): WorkspaceProxy<W> =>
  makeRoot('workspace') as WorkspaceProxy<W>;
